// Generic bottom-sheet confirmation modal — replaces window.confirm()
// for destructive / "are you sure" UX in modern theme.
//
// Usage:
//   const [open, setOpen] = useState(false);
//   <ConfirmSheet
//     open={open}
//     onClose={() => setOpen(false)}
//     title="..."
//     body={<p>...</p>}
//     confirmLabel="..."
//     onConfirm={async () => { ... }}
//   />
//
// Confirm button can be styled "primary" (default rose) or "danger"
// (red) for destructive ops.

import { useEffect, useState, type ReactNode } from "react";

export function ConfirmSheet({
  open,
  onClose,
  title,
  body,
  confirmLabel,
  cancelLabel = "Скасувати",
  onConfirm,
  tone = "primary",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  tone?: "primary" | "danger";
}) {
  const [busy, setBusy] = useState(false);

  // Escape закриває (тільки коли не в процесі виконання — щоб
  // випадковий тап Esc не лишив операцію в підвішеному стані).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  async function handleConfirm() {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="nz-dim" onClick={() => !busy && onClose()}>
      <div
        className="nz-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="nz-sheet-grip" />
        <div className="nz-sheet-title">{title}</div>
        {body && <div className="nz-confirm-body">{body}</div>}
        <button
          type="button"
          className={
            tone === "danger"
              ? "nz-confirm-btn-danger"
              : "nz-confirm-btn-primary"
          }
          onClick={handleConfirm}
          disabled={busy}
        >
          {busy ? "Зачекай…" : confirmLabel}
        </button>
        <button
          type="button"
          className="nz-sheet-cancel"
          onClick={onClose}
          disabled={busy}
        >
          {cancelLabel}
        </button>
      </div>
    </div>
  );
}
